// Approvals, runs, wake-ups, delegation requests, a DE's agents and budget, agent-key rotation
// (04 §Approvals, runs, agents; 02 §Approvals, runs, cost; ADR-0021). M3-01 contracts: handlers
// are M3-02 (runs, wake-ups, admission), M3-03 (approvals, delegation), M3-04 (DE budget) and
// M3-02 (key rotation). Everything here is the `agents` add-on (ADR-0016) except the DE budget,
// which is finance (core).
import { z } from "zod";
import {
  AgentProfile,
  Approval,
  ApprovalCardInput,
  ApprovalProposal,
  Budget,
  ContextRef,
  DelegationRequest,
  RulingConditionInput,
  Run,
  RunHandBack,
  RunStep,
  SourceRefJson,
  WakeTimer,
  WakeupRequest,
} from "../agent-rows";
import { ApprovalRead } from "../approvals";
import { RunDecision } from "../de-config-rows";
import {
  AgentProfileRole,
  ApprovalKind,
  ApprovalState,
  PageKind,
  ReplyLength,
  RunEffortRequest,
  RunState,
  RunStepKind,
  SandboxKind,
  WakeupKind,
} from "../enums";
import {
  BoundedJson,
  LIMITS,
  MicrosAmount,
  MinorAmount,
  Timestamp,
  text,
  Uuid,
} from "../primitives";
import { BudgetPaceSettings, CompanyBudgetPace } from "../rows";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { BudgetInput } from "./finance";
import { LoopVerdict } from "./loop-breaker";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const agents = { feature: "agents" } as const;

// Approvals -------------------------------------------------------------------------------------

export const ApprovalListQuery = PageQuery.extend({
  state: ApprovalState.optional(),
  approver: z.enum(["me"]).optional().describe("Only approvals the caller's seats decide"),
  kind: ApprovalKind.optional(),
});

/** `POST approvals` (04): anyone proposing on behalf of a seat; a run's key proposes for its own. */
export const ApprovalCreate = z
  .strictObject({
    // AGENT-REPORTING: `kpi_value` approvals are raised by `outcomes.record` only.
    // D15: `memory_lesson` approvals by `memory.agentRemember` only.
    // AGENT-PLUMBING: `agent_cost` approvals by `runs.reportCost` only.
    // RECALL-RULES: `memory_rule` approvals by `memoryRules.create` and `memory.agentSuggestRule`.
    kind: ApprovalKind.exclude([
      "budget_overrun",
      "kpi_value",
      "memory_lesson",
      "agent_cost",
      "memory_rule",
    ]),
    subject_kind: z.string().min(1).max(64),
    subject_id: Uuid,
    requested_by_seat_id: Uuid.optional(),
    approver_seat_id: Uuid.optional().describe(
      "A person's choice (default: the requesting seat's supervisor); ignored for an agent, whose approver the server derives (the task's owner, else the kind's rule)",
    ),
    proposal: ApprovalProposal,
    expires_at: Timestamp.nullable().optional(),
    card: ApprovalCardInput.optional().describe(
      "CORE-CARDS (flag `escalation_cards`; ignored while off): situation, 2–3 options with their consequences, the recommended option, an optional deadline",
    ),
    condition: RulingConditionInput.optional().describe(
      "CORE-PRECEDENTS (flag `precedents`; ignored while off): the case's structured facts (and class, scope), matched against the company's rulings at filing",
    ),
  })
  .meta({ id: "ApprovalCreate" });
/** CORE-PRECEDENTS: "apply to similar cases" (default: the company's setting for rejections). */
const Standing = z
  .boolean()
  .optional()
  .describe(
    "CORE-PRECEDENTS (flag `precedents`): keep this decision as a standing ruling for similar cases",
  );
export const ApprovalReject = z
  .strictObject({ reason: text(LIMITS.text), standing: Standing })
  .meta({ id: "ApprovalReject" });
/**
 * CORE-CARDS: decide a card by one of its options — an `approve` option approves (with its
 * `change`, if any), a `reject` option rejects (the reason defaults to the option's label).
 */
export const ApprovalChoose = z
  .strictObject({
    option: z.string().min(1).max(40),
    reason: z.string().max(LIMITS.text).optional(),
    standing: Standing,
  })
  .meta({ id: "ApprovalChoose" });
/** B2-GATES: "send back with a note" — the DE is asked to rework it (woken with the note). */
export const ApprovalSendBack = z
  .strictObject({ note: text(LIMITS.text) })
  .meta({ id: "ApprovalSendBack" });
/** Approve with edits: the edited proposal becomes the immutable `approved_payload`. */
export const ApprovalEdit = z
  .strictObject({ proposal: ApprovalProposal, reason: z.string().max(LIMITS.text).optional() })
  .meta({ id: "ApprovalEdit" });
/** `GET my/approvals/count`: how many `my/approvals` lists, without the rows (badges). */
export const ApprovalCount = z.object({ count: z.int().min(0) }).meta({ id: "ApprovalCount" });

// Runs ------------------------------------------------------------------------------------------

/** `POST runs` (04; agent runtime): admission reserves budget before the run exists. */
export const RunCreate = z
  .strictObject({
    seat_id: Uuid.optional(),
    crew_agent_id: Uuid.optional(),
    trigger: z.strictObject({ kind: WakeupKind, ref: SourceRefJson.nullable().optional() }),
    wakeup_ids: z.array(Uuid).max(LIMITS.list).optional(),
    parent_run_id: Uuid.optional(),
    estimate_micros: MicrosAmount.min(1).optional().describe("Default: min_estimate(model)"),
    model: z.string().max(LIMITS.key).optional(),
    effort: RunEffortRequest.optional().describe(
      "RUN-EFFORT: low | medium | high, or auto (the default): the system picks the tier",
    ),
  })
  .refine((r) => (r.seat_id === undefined) !== (r.crew_agent_id === undefined), {
    message: "exactly one of seat_id and crew_agent_id",
  })
  .meta({ id: "RunCreate" });

export const RunListQuery = PageQuery.extend({
  state: RunState.optional(),
  seat_id: Uuid.optional(),
  membership_id: Uuid.optional(),
});

/** How many decision-log rows `runs.get` inlines; `runs/{id}/decisions` pages the rest (DC-07). */
export const RUN_DETAIL_DECISIONS = 100;

/**
 * A run with its steps; step payloads are redacted by field audience for people (04). DC-07
 * (ADR-0019): the verdict is on the run itself (`verdict`, `verdict_evidence_ref`,
 * `output_ref`); the decision log's first rows by `seq` ride along, `decisions_truncated` when
 * the log is longer (page it with `runs/{id}/decisions`).
 */
export const RunDetail = Run.extend({
  steps: z.array(RunStep),
  decisions: z.array(RunDecision).max(RUN_DETAIL_DECISIONS),
  decisions_truncated: z.boolean(),
}).meta({ id: "RunDetail" });

const StepInput = z.strictObject({
  kind: RunStepKind,
  payload: BoundedJson.optional(),
  model: z.string().max(LIMITS.key).optional(),
  tokens_in: z.int().min(0).optional(),
  tokens_out: z.int().min(0).optional(),
  cost_micros: MicrosAmount.min(0).optional(),
  duration_ms: z.int().min(0).optional(),
});
/** `POST runs/{id}/steps` (append, batched); the first batch of a queued run starts it. */
export const RunStepsAppend = z
  .strictObject({ steps: z.array(StepInput).min(1).max(LIMITS.list) })
  .meta({ id: "RunStepsAppend" });
export const RunStepsAppended = z
  .object({
    run: Run,
    steps: z.array(RunStep),
    /** LOOP-BREAKER: the loop verdict when the steps tripped it (a warning, or the stop). */
    loop: LoopVerdict.optional(),
  })
  .meta({ id: "RunStepsAppended" });

export const RunWait = z
  .strictObject({
    reason: z.enum(["input", "approval", "children", "budget"]),
    approval_id: Uuid.optional(),
  })
  .meta({ id: "RunWait" });
export const RunComplete = z
  .strictObject({
    summary: z.string().max(LIMITS.text).optional(),
    hand_back: RunHandBack.optional(),
    cost: z
      .strictObject({
        cost_micros: MicrosAmount.min(0),
        tokens_in: z.int().min(0),
        tokens_out: z.int().min(0),
      })
      .optional(),
    /**
     * T2-BOARD-WAKE: a board run's ack — the last board journal `seq` it handled. The board's
     * ack moves forward only (never past the journal); later events stay for the next wake.
     */
    board_seq_acked: z
      .int()
      .min(0)
      .optional()
      .describe("A board run: the last board event seq handled (get_board_events)"),
  })
  .meta({ id: "RunComplete" });
export const RunFail = z
  .strictObject({ reason: text(LIMITS.name), detail: z.string().max(LIMITS.text).optional() })
  .meta({ id: "RunFail" });
export const RunDelegate = z
  .strictObject({
    to_seat_id: Uuid.optional(),
    to_membership_id: Uuid.optional(),
    task_id: Uuid.optional(),
    budget_micros: MicrosAmount.min(0),
    deadline_at: Timestamp.optional(),
    context_refs: z.array(ContextRef).max(LIMITS.list).optional(),
    message: z.string().max(LIMITS.text).optional(),
    effort: RunEffortRequest.optional().describe(
      "RUN-EFFORT: how hard the recipient's run works — low | medium | high, or auto (the default)",
    ),
    check_only: z
      .boolean()
      .optional()
      .describe(
        "Hold every rule and answer 200 {ok: true} without sending (the MCP delegate asks first, before it creates the child task)",
      ),
  })
  .refine((d) => (d.to_seat_id === undefined) !== (d.to_membership_id === undefined), {
    message: "exactly one of to_seat_id and to_membership_id",
  })
  .meta({ id: "RunDelegate" });
/**
 * DC-08 (05 §Delegation rules 8): spawn an ephemeral worker of the run's DE at depth + 1. The
 * server copies in the DE's standing orders, verbatim, from the deployed bundle; nothing else of
 * the lead travels. The worker's token reads only `context_refs`.
 */
export const RunSpawnWorker = z
  .strictObject({
    profile_id: Uuid.describe("A live `worker` profile (kind `ephemeral`) of the run's DE"),
    context_refs: z.array(ContextRef).max(LIMITS.list),
    budget_micros: MicrosAmount.min(1).describe("Carved from what the run may still hand out"),
    deadline_at: Timestamp.optional().describe("Default: 24 h"),
  })
  .meta({ id: "RunSpawnWorker" });
export const RunSandbox = z
  .strictObject({
    action: z.enum(["start", "stop"]),
    kind: SandboxKind,
    duration_ms: z.int().min(0).optional().describe("`stop` only"),
    cost_micros: MicrosAmount.min(0).optional().describe("`stop` only"),
  })
  .meta({ id: "RunSandbox" });
/**
 * AGENT-PLUMBING: `POST runs/{id}/costs` — a paid non-model service the run used or is about to
 * use (a stock-photo or voice API). Amounts are in the company's reporting currency (no FX).
 */
export const RunReportCost = z
  .strictObject({
    amount_micros: MicrosAmount.min(1)
      .max(1_000_000_000_000)
      .describe("Millionths of a currency unit: 0.52 USD is 520000"),
    currency: z
      .string()
      .length(3)
      .regex(/^[A-Z]{3}$/)
      .describe("ISO 4217; must be the company's reporting currency"),
    vendor: text(120).describe("Who was paid (e.g. Shutterstock, ElevenLabs)"),
    purpose: text(500).describe("What it was for"),
    external_ref: text(200).optional().describe("The vendor's receipt or request id"),
    // WALLET-P1: what kind of spend it was, for prepaid-credit pricing and the client's view.
    category: z
      .string()
      .max(40)
      .regex(/^[a-z][a-z0-9_]{1,39}$/)
      .optional()
      .describe(
        "What kind of spend: video_minute, audio_minute, tts_1k_chars, web_search, web_fetch, or your own (e.g. stock_photo)",
      ),
    quantity: z
      .number()
      .min(0)
      .max(1_000_000_000)
      .optional()
      .describe("How many of the category's unit (minutes, thousands of characters, calls)"),
  })
  .meta({ id: "RunReportCost" });
export const ReportedCost = z
  .strictObject({
    status: z
      .enum(["booked", "pending_approval"])
      .describe("`booked`: on the ledger as reported by the agent; else an approval was filed"),
    cost_event_id: Uuid.nullable(),
    approval_id: Uuid.nullable(),
    amount_micros: MicrosAmount,
    currency: z.string(),
    /** What the run may still book by itself (its reservation left, in micros). */
    remaining_micros: MicrosAmount.nullable(),
  })
  .meta({ id: "ReportedCost" });
/** AGENT-PLUMBING: `POST runs/{id}/wake-after` — wake this DE on the same subject later. */
export const RunWakeAfter = z
  .strictObject({
    minutes: z.int().min(1).max(1440).describe("1 minute to 24 hours"),
    reason: text(500).describe("Why, e.g. 'waiting on 3 helper cards'"),
  })
  .meta({ id: "RunWakeAfter" });
/**
 * DE-QUICK-ACK: `POST runs/{id}/quick-reply` — the runtime's quick first reply to a person's
 * message, before the main run: the light model's raw answer to the prompt CompanyOS sent (the
 * wake item's `quickReply`, the wake-up's `quick_reply`) and what the call cost. CompanyOS reads
 * the answer, checks every guard again, posts it in the message's thread, and books the call as
 * the DE's cost (a `model_call` step tagged `quick_reply`).
 *
 * QUICK-REPLY-COMPANY-KEY: with `mode: "server"` or without `output` (a self-hosted DE's bridge) CompanyOS drafts the
 * reply itself, with the company's light model and model access (no key of the runtime's); the
 * call's figures are then CompanyOS's own and the ones sent here are ignored.
 */
export const RunQuickReply = z
  .strictObject({
    message_id: Uuid.describe("The person's message the reply answers (one this run was woken by)"),
    output: z
      .string()
      .max(4_000)
      .optional()
      .describe(
        "The light model's raw answer (one line of JSON); omitted: CompanyOS drafts the reply",
      ),
    /** `server`: CompanyOS drafts the reply (any `output` and figures sent are ignored). */
    mode: z.enum(["server"]).optional(),
    model: z.string().min(1).max(200).nullable().optional(),
    tokens_in: z.int().min(0).max(1_000_000).optional(),
    tokens_out: z.int().min(0).max(1_000_000).optional(),
    cost_micros: MicrosAmount.min(0).max(10_000_000).optional(),
    duration_ms: z.int().min(0).max(600_000).optional(),
  })
  .meta({ id: "RunQuickReply" });
export const QuickReplyResult = z
  .strictObject({
    /**
     * `posted`: in the thread; `none`: the model saw nothing to say first; `skipped`: a guard
     * stopped it (`reason`).
     */
    outcome: z.enum(["posted", "none", "skipped"]),
    reason: z
      .enum([
        "not_a_person",
        "message_gone",
        "turned_off",
        "already_sent",
        "replied_recently",
        "held_for_approval",
        "agent_room",
        "no_access",
        // QUICK-REPLY-COMPANY-KEY (CompanyOS drafts): no light model or model access for the
        // company; the call failed or timed out (nothing booked).
        "no_model",
        "model_failed",
      ])
      .nullable(),
    /**
     * QUICK-REPLY-NATURAL: `done` the whole answer (the main run sends no second message); `ack`
     * a first thought plus "I'll dig in"; `ask` the one question that matters.
     */
    kind: z.enum(["done", "ack", "ask"]).nullable(),
    /** What was posted (null unless posted). */
    text: z.string().nullable(),
    message_id: Uuid.nullable(),
    /** `ask` only: what the DE goes with meanwhile (null: it waits for the answer). */
    assumption: z.string().nullable(),
    /** `ask` only: the main run should wait for the person's answer. */
    wait: z.boolean(),
    /** The model call's step (null when nothing was booked). */
    step_id: Uuid.nullable(),
  })
  .meta({ id: "QuickReplyResult" });
// Wake-ups and delegation -----------------------------------------------------------------------

export const WakeupCreate = z
  .strictObject({
    membership_id: Uuid.describe("The agent to wake"),
    kind: WakeupKind,
    source_ref: SourceRefJson.optional(),
    idempotency_key: z.string().min(1).max(128).optional(),
  })
  .meta({ id: "WakeupCreate" });
export const WakeupPendingQuery = z.object({
  agent: Uuid.optional().describe("One agent membership; default every agent of the key"),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

/**
 * WF-FIX (2026-10-26): `GET wakeups/inbox` (managers). Admission leaves a self-hosted DE's
 * wake-ups queued for it to pull (`wakeups/pending`, MCP `my_inbox`), so no queued run counts
 * them: one row per self-hosted DE with queued wake-ups, oldest first; `total` sums `queued`.
 */
export const WakeupInbox = z
  .object({
    data: z.array(
      z.object({
        membership_id: Uuid,
        display_name: z.string(),
        queued: z.int().min(0),
        oldest_requested_at: Timestamp,
      }),
    ),
    total: z.int().min(0),
  })
  .meta({ id: "WakeupInbox" });

/** M3-06: `POST wakeups/dispatch` (the runtime's service key; the poll fallback's claim). */
export const WakeupDispatch = z
  .strictObject({
    agent: Uuid.optional().describe("One agent membership; default every agent with queued work"),
    limit: z.int().min(1).max(50).optional().describe("Queued runs returned (default 20)"),
  })
  .meta({ id: "WakeupDispatch" });

/**
 * What the agent runtime is told about an admitted run (ADR-0021 §2): the signed webhook's body,
 * and each item of `wakeups/dispatch`. Carries the run's capability token (the runtime's
 * service key only).
 */
export const RuntimeWakeup = z
  .object({
    run_id: Uuid,
    company_id: Uuid,
    membership_id: Uuid,
    seat_id: Uuid.nullable(),
    shape: z.enum(["A", "B"]),
    adapter: z.enum(["hermes_gateway", "claude_local", "codex_local", "hermes"]),
    capability_token: z.string(),
    deadline_at: Timestamp,
    budget_reserved_micros: MicrosAmount,
    trigger: z.object({ kind: z.string(), ref: SourceRefJson.nullable() }),
    wakeup_ids: z.array(Uuid),
    model: z.object({ path: z.literal("gateway"), alias: z.string().nullable() }),
    target: z.union([
      z.object({
        instance_id: z.string(),
        profile_name: z.string(),
        session_key: z.string(),
        /**
         * Phase 2 increment 6: the endpoint and container key copied from the routing evidence
         * (a passed probe), never recomputed; plain http only for the overlay.
         */
        endpoint: z
          .object({
            api_base_url: z.string(),
            api_key: z.string(),
            allow_plain_http: z.boolean(),
          })
          .optional(),
      }),
      z.object({ image: z.string().nullable(), skill_packs: z.array(z.string()) }),
    ]),
    delegation_depth: z.int().min(0).optional(),
    /**
     * DC-08: an ephemeral worker's run — the DE's standing orders verbatim (the only thing of the
     * DE it receives), the refs its token reads and the bundle the block came from.
     */
    worker: z
      .object({
        standing_orders: z.string(),
        context_refs: z.array(ContextRef),
        bundle_sha: z.string(),
      })
      .optional(),
    /**
     * DOCS-KB decision 7: the agent's knowledge base, resolved at dispatch to page text within
     * the caps (12,000 characters a document, 48,000 in all); absent when it has none. The
     * runtime renders it as data, never as instructions.
     */
    knowledge: z
      .array(
        z.object({
          page_id: Uuid,
          title: z.string(),
          kind: PageKind,
          space_name: z.string(),
          updated_at: Timestamp,
          text: z.string(),
          truncated: z.boolean(),
        }),
      )
      .max(50)
      .optional(),
    /** DE-REPLY-STYLE: how the person this run answers wants chat replies. */
    reply_style: z.object({ length: ReplyLength, guide: z.string().min(1).max(4_000) }).optional(),
  })
  .meta({ id: "RuntimeWakeup" });

export const DelegationDecline = z
  .strictObject({ reason: z.string().max(LIMITS.text).optional() })
  .meta({ id: "DelegationDecline" });
export const DelegationComplete = z
  .strictObject({ result: RunHandBack })
  .meta({ id: "DelegationComplete" });

// A DE's agents (HTVA composition; never shown in the client company UI) and budget ----------

export const AgentProfileCreate = z
  .strictObject({
    template_id: Uuid,
    template_version: z.int().min(1),
    role_in_owner: AgentProfileRole,
    overrides: BoundedJson.optional(),
    memory_group_id: z.string().max(LIMITS.key).optional(),
  })
  .meta({ id: "AgentProfileCreate" });
export const AgentProfilePatch = z
  .strictObject({
    role_in_owner: AgentProfileRole.optional(),
    template_version: z.int().min(1).optional(),
    overrides: BoundedJson.optional(),
    memory_group_id: z.string().max(LIMITS.key).nullable().optional(),
  })
  .meta({ id: "AgentProfilePatch" });
export const AgentProfileParams = z.object({ co: Uuid, id: Uuid, profile_id: Uuid });

/**
 * DE-BUDGET-PACE: a DE's spend against its limit this period and whether it lasts. The limit is
 * the effective budget's hard cap, else its amount. `projected_minor` is the period's spend at the
 * current pace (`basis`: the period's average so far, or the last 7 days once the period is
 * 7 days in); `runout_at` when the limit runs out at that pace (null: it lasts). `status`:
 * `on_track`, `ahead_of_pace` (the projection reached `alert_at` × limit with `min_used` spent),
 * `reserve_reached` (spend reached limit − reserve: no new runs start) or `no_budget`.
 */
export const BudgetPaceStatus = z
  .enum(["on_track", "ahead_of_pace", "reserve_reached", "no_budget"])
  .meta({ id: "BudgetPaceStatus" });
export const BudgetPace = z
  .object({
    status: BudgetPaceStatus,
    budget_id: Uuid.nullable(),
    inherited: z.boolean(),
    currency: z.string().nullable(),
    limit_minor: MinorAmount.nullable(),
    /** Whether the limit is a hard cap (only a cap stops runs at the reserve). */
    capped: z.boolean(),
    spent_minor: MinorAmount,
    used: z.number(),
    reserve_minor: MinorAmount.nullable(),
    period_start: Timestamp.nullable(),
    period_end: Timestamp.nullable(),
    days_in: z.number(),
    days_left: z.number().nullable(),
    projected_minor: MinorAmount.nullable(),
    basis: z.enum(["period_average", "last_7_days"]).nullable(),
    runout_at: Timestamp.nullable(),
    /** The thresholds in force (company default over the platform's, the DE's own over both). */
    settings: z.object({
      alert_at: z.number(),
      min_used: z.number(),
      second_at: z.number(),
      reserve: z.number(),
    }),
    /** The DE's own overrides (`settings.budget_pace.for_de`). */
    overrides: BudgetPaceSettings,
  })
  .meta({ id: "BudgetPace" });
export type BudgetPace = z.infer<typeof BudgetPace>;

/** `GET digital-employees/{id}/budget`: the DE's holder budget and its spend this period. */
export const DigitalEmployeeBudget = z
  .object({
    budget: Budget.nullable(),
    period_key: z.string().nullable(),
    actual_micros: MicrosAmount,
    reserved_micros: MicrosAmount,
    paused_for_budget: z.boolean(),
    pace: BudgetPace,
    /**
     * BUDGET-CAP: every `*_minor` amount is integer MINOR units of the budget's currency (cents
     * for USD: 500 = $5.00); `minor_per_unit` minor units make one unit of the currency.
     */
    amount_unit: z.literal("minor"),
    minor_per_unit: z.int(),
    /** `cap`: new runs stop at `cap_minor`; `alert`: it only warns. Null: no budget. */
    mode: z.enum(["alert", "cap"]).nullable(),
    /** The stop point in cap mode (the hard cap, else the amount); the limit in alert mode. */
    cap_minor: MinorAmount.nullable(),
    /** When the period's spend starts again from zero; null for a standing budget. */
    resets_at: Timestamp.nullable(),
  })
  .meta({ id: "DigitalEmployeeBudget" });

/** `POST agent-keys/rotate` (by the key itself): new plaintext once; the old key lives 10 min. */
export const AgentKeyRotated = z
  .object({
    key: z.object({ id: Uuid, prefix: z.string().length(12) }),
    plaintext: z.string().regex(/^cosk_(de|svc)_/),
    old_valid_until: Timestamp,
  })
  .meta({ id: "AgentKeyRotated" });

// Routes ----------------------------------------------------------------------------------------

const runSub = (path: string) => `${base}/runs/{id}/${path}`;
const runWrite = (id: string, path: string, summary: string) =>
  ({ id, method: "post", path: runSub(path), tag: "runs", summary }) as const;

export const agentRoutes = {
  "approvals.list": defineRoute({
    ...agents,
    id: "approvals.list",
    method: "get",
    path: `${base}/approvals`,
    tag: "approvals",
    summary: "Approvals (`?state=pending&approver=me` is the queue)",
    access: "member",
    params: CompanyParams,
    query: ApprovalListQuery,
    // Parity P6a: each row says whether the caller may view and decide it (§3.1 cards)
    response: { status: 200, schema: page(ApprovalRead) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "approvals.create": defineRoute({
    ...agents,
    id: "approvals.create",
    method: "post",
    path: `${base}/approvals`,
    tag: "approvals",
    summary: "Propose a change for a seat's approver (also inline from tasks and messages)",
    access: "member",
    params: CompanyParams,
    body: ApprovalCreate,
    idempotency: "optional",
    response: { status: 201, schema: Approval },
    // DC-01 (ADR-0019 §2): a run's approval is refused unless its verdict is `verified`.
    // STEP1-OUTBOX: an external action through a connection its requester holds no grant on.
    errors: [
      "run_not_owned",
      "verdict_missing",
      "verdict_not_verified",
      "verdict_inconclusive",
      "connection_grant_missing",
    ],
    emits: ["approval.requested"],
  }),
  "approvals.get": defineRoute({
    ...agents,
    id: "approvals.get",
    method: "get",
    path: `${base}/approvals/{id}`,
    tag: "approvals",
    summary: "Get an approval",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ApprovalRead },
    errors: [],
    emits: [],
  }),
  "approvals.approve": defineRoute({
    ...agents,
    id: "approvals.approve",
    method: "post",
    path: `${base}/approvals/{id}/approve`,
    tag: "approvals",
    summary: "Approve (idempotent: a second decision returns the first)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Approval },
    errors: ["approval_expired"],
    emits: [
      "approval.decided",
      "budget.overrun_approved",
      "budget.changed",
      "digital_employee.resumed",
      // AGENT-REPORTING: a `kpi_value` approval records the reviewed value
      "observation.recorded",
      "outcome.recorded",
      "activity.recorded",
    ],
  }),
  "approvals.reject": defineRoute({
    ...agents,
    id: "approvals.reject",
    method: "post",
    path: `${base}/approvals/{id}/reject`,
    tag: "approvals",
    summary: "Reject with a reason (idempotent)",
    access: "member",
    params: CompanyItemParams,
    body: ApprovalReject,
    idempotency: "optional",
    response: { status: 200, schema: Approval },
    errors: ["approval_expired"],
    emits: ["approval.decided"],
  }),
  // CORE-CARDS: the decision as a card option (approve or reject per the option); the chosen key
  // is stored as `chosen_option`.
  "approvals.choose": defineRoute({
    ...agents,
    id: "approvals.choose",
    method: "post",
    path: `${base}/approvals/{id}/choose`,
    tag: "approvals",
    summary: "Decide by one of the card's options (idempotent)",
    access: "member",
    params: CompanyItemParams,
    body: ApprovalChoose,
    idempotency: "optional",
    response: { status: 200, schema: Approval },
    errors: ["approval_expired"],
    emits: ["approval.decided"],
  }),
  // B2-GATES: a named decision — rejected with `sent_back`, the note as the reason, posted to the
  // task when the approval is about one; the requesting DE is woken with it.
  "approvals.sendBack": defineRoute({
    ...agents,
    id: "approvals.sendBack",
    method: "post",
    path: `${base}/approvals/{id}/send-back`,
    tag: "approvals",
    summary: "Send back with a note (the DE reworks it; idempotent)",
    access: "member",
    params: CompanyItemParams,
    body: ApprovalSendBack,
    idempotency: "optional",
    response: { status: 200, schema: Approval },
    errors: ["approval_expired"],
    emits: ["approval.decided", "message.created"],
  }),
  "approvals.edit": defineRoute({
    ...agents,
    id: "approvals.edit",
    method: "post",
    path: `${base}/approvals/{id}/edit`,
    tag: "approvals",
    summary: "Approve with edits (the edited proposal is what executes; idempotent)",
    access: "member",
    params: CompanyItemParams,
    body: ApprovalEdit,
    idempotency: "optional",
    response: { status: 200, schema: Approval },
    errors: ["approval_expired"],
    emits: [
      "approval.decided",
      "budget.overrun_approved",
      "budget.changed",
      "digital_employee.resumed",
      // AGENT-REPORTING: a `kpi_value` approval records the reviewed value
      "observation.recorded",
      "outcome.recorded",
      "activity.recorded",
    ],
  }),
  "my.approvals": defineRoute({
    ...agents,
    id: "my.approvals",
    method: "get",
    path: `${base}/my/approvals`,
    tag: "approvals",
    summary: "Pending approvals the caller's seats decide, across subjects",
    access: "member",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(Approval) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "my.approvalsCount": defineRoute({
    ...agents,
    id: "my.approvalsCount",
    method: "get",
    path: `${base}/my/approvals/count`,
    tag: "approvals",
    summary: "How many pending approvals the caller's seats decide (the sidebar badge)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: ApprovalCount },
    errors: [],
    emits: [],
  }),

  "runs.list": defineRoute({
    ...agents,
    id: "runs.list",
    method: "get",
    path: `${base}/runs`,
    tag: "runs",
    summary: "Runs the caller can view",
    access: "member",
    params: CompanyParams,
    query: RunListQuery,
    response: { status: 200, schema: page(Run) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "runs.create": defineRoute({
    ...agents,
    id: "runs.create",
    method: "post",
    path: `${base}/runs`,
    tag: "runs",
    summary: "Admit a run (reserves budget over the chain; refused without room)",
    access: "agent_key",
    params: CompanyParams,
    body: RunCreate,
    idempotency: "required",
    response: { status: 201, schema: Run },
    // DC-01 (ADR-0020 widened): a refused model path or credential, never a silent fallback.
    errors: [
      "budget_exhausted",
      // WALLET-P1: the company's prepaid credit cannot cover the run's reservation.
      "wallet_empty",
      "kind_not_allowed",
      "subscription_path_refused",
      "credential_path_disabled",
      "attestation_required",
      // MANAGED-POLICY: the agent policy refuses the run at dispatch (the trigger's board).
      "policy_denied",
    ],
    emits: ["run.queued", "budget.reserved", "wakeup.coalesced"],
  }),
  "runs.get": defineRoute({
    ...agents,
    id: "runs.get",
    method: "get",
    path: `${base}/runs/{id}`,
    tag: "runs",
    summary: "A run with its steps (payloads redacted by field audience)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: RunDetail },
    errors: [],
    emits: [],
  }),
  "runs.appendSteps": defineRoute({
    ...agents,
    ...runWrite(
      "runs.appendSteps",
      "steps",
      "Append steps (batched); the first batch starts the run",
    ),
    access: "run_token",
    params: CompanyItemParams,
    body: RunStepsAppend,
    idempotency: "required",
    response: { status: 200, schema: RunStepsAppended },
    errors: ["run_not_owned", "run_state_conflict", "budget_exhausted"],
    // LOOP-BREAKER: the steps feed the loop detector (a warning once, then the stop).
    emits: [
      "run.step_recorded",
      "run.started",
      "run.loop_warned",
      "run.loop_stopped",
      "run.failed",
      "budget.settled",
    ],
  }),
  "runs.wait": defineRoute({
    ...agents,
    ...runWrite("runs.wait", "wait", "Park the run (input, approval, children, budget)"),
    access: "run_token",
    params: CompanyItemParams,
    body: RunWait,
    response: { status: 200, schema: Run },
    errors: ["run_not_owned", "run_state_conflict"],
    emits: ["run.waiting"],
  }),
  "runs.complete": defineRoute({
    ...agents,
    ...runWrite(
      "runs.complete",
      "complete",
      "Complete with a summary and hand-back; settles budget",
    ),
    access: "run_token",
    params: CompanyItemParams,
    body: RunComplete,
    idempotency: "required",
    response: { status: 200, schema: Run },
    errors: ["run_not_owned", "run_state_conflict"],
    emits: ["run.completed", "budget.settled"],
  }),
  "runs.fail": defineRoute({
    ...agents,
    ...runWrite("runs.fail", "fail", "Fail the run; settles budget at recorded spend"),
    access: "run_token",
    params: CompanyItemParams,
    body: RunFail,
    idempotency: "required",
    response: { status: 200, schema: Run },
    errors: ["run_not_owned", "run_state_conflict"],
    emits: ["run.failed", "budget.settled"],
  }),
  "runs.cancel": defineRoute({
    ...agents,
    ...runWrite("runs.cancel", "cancel", "Cancel a run (people); releases or settles its budget"),
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Run },
    errors: ["run_state_conflict"],
    emits: ["run.cancelled", "budget.settled", "budget.released"],
  }),
  // FAILURE-STOP: a person continues a run the MCP server stopped after repeated failures (its
  // error counters and the stop are cleared; the run is live again for its agent).
  "runs.continueAfterFailures": defineRoute({
    ...agents,
    ...runWrite(
      "runs.continueAfterFailures",
      "failures/continue",
      "Continue a run stopped after repeated failures (people); clears its error counters",
    ),
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Run },
    errors: ["run_state_conflict"],
    emits: ["run.failures_continued", "wakeup.requested"],
  }),
  "runs.delegate": defineRoute({
    ...agents,
    ...runWrite("runs.delegate", "delegate", "Delegate work to a seat or member (depth-limited)"),
    access: "run_token",
    params: CompanyItemParams,
    body: RunDelegate,
    idempotency: "required",
    response: { status: 201, schema: DelegationRequest },
    errors: ["run_not_owned", "delegation_loop", "budget_exhausted"],
    emits: ["delegation_request.sent", "run.delegated", "wakeup.requested"],
  }),
  // DC-08: an ephemeral worker (05 §Delegation rules 8). A refusal is 422 `worker_refused`; its
  // reason reaches the admission log as `run.worker_refused` (written after the rollback).
  "runs.spawnWorker": defineRoute({
    ...agents,
    ...runWrite(
      "runs.spawnWorker",
      "workers",
      "Spawn an ephemeral worker with the DE's standing orders, scoped to the listed refs",
    ),
    access: "run_token",
    params: CompanyItemParams,
    body: RunSpawnWorker,
    idempotency: "required",
    response: { status: 201, schema: Run },
    errors: ["run_not_owned", "run_state_conflict", "worker_refused"],
    emits: ["run.queued", "budget.reserved"],
  }),
  "runs.sandbox": defineRoute({
    ...agents,
    ...runWrite(
      "runs.sandbox",
      "sandbox",
      "Record a sandbox start or stop (the sandbox charge basis)",
    ),
    access: "run_token",
    params: CompanyItemParams,
    body: RunSandbox,
    idempotency: "required",
    response: { status: 200, schema: Run },
    errors: ["run_not_owned", "run_state_conflict"],
    emits: ["sandbox.started", "sandbox.stopped"],
  }),
  // AGENT-PLUMBING: an agent-reported paid service (MCP `report_cost`). Within the run's
  // reservation and the auto-book limit it is booked (`cost.posted`, source `agent_reported`);
  // otherwise an `agent_cost` approval is filed for the DE's supervisor.
  "runs.reportCost": defineRoute({
    ...agents,
    ...runWrite("runs.reportCost", "costs", "Report a paid non-model service the run used"),
    access: "run_token",
    params: CompanyItemParams,
    body: RunReportCost,
    idempotency: "required",
    response: { status: 201, schema: ReportedCost },
    errors: [
      "run_not_owned",
      "run_state_conflict",
      "currency_mismatch",
      "supervisor_required",
      "wallet_empty",
      "meter_conflict",
      "conflict",
    ],
    emits: ["cost.posted", "approval.requested"],
  }),
  // AGENT-PLUMBING: the wait-for-helpers pattern (MCP `wake_me_after`): one pending timer per
  // DE and subject; asking again moves it.
  "runs.wakeAfter": defineRoute({
    ...agents,
    ...runWrite(
      "runs.wakeAfter",
      "wake-after",
      "Wake this DE on the run's subject after N minutes",
    ),
    access: "run_token",
    params: CompanyItemParams,
    body: RunWakeAfter,
    idempotency: "required",
    response: { status: 201, schema: WakeTimer },
    errors: ["run_not_owned", "run_state_conflict", "too_many_timers"],
    emits: ["wakeup.scheduled"],
  }),
  // DE-QUICK-ACK: the instant first reply ("On it — …") before the main run.
  "runs.quickReply": defineRoute({
    ...agents,
    ...runWrite(
      "runs.quickReply",
      "quick-reply",
      "Post the DE's quick first reply to the message that woke the run, and book its model call",
    ),
    access: "run_token",
    params: CompanyItemParams,
    body: RunQuickReply,
    idempotency: "required",
    response: { status: 200, schema: QuickReplyResult },
    errors: ["run_not_owned", "run_state_conflict", "budget_exhausted"],
    emits: ["run.step_recorded", "run.started", "message.created"],
  }),

  "wakeups.create": defineRoute({
    ...agents,
    id: "wakeups.create",
    method: "post",
    path: `${base}/wakeups`,
    tag: "runs",
    summary: "Request a wake-up (internal, or an agent's follow-up with X-CompanyOS-Run)",
    access: "agent_key",
    params: CompanyParams,
    body: WakeupCreate,
    response: { status: 201, schema: WakeupRequest },
    errors: ["run_not_owned", "subject_closed"],
    emits: ["wakeup.requested"],
  }),
  "wakeups.pending": defineRoute({
    ...agents,
    id: "wakeups.pending",
    method: "get",
    path: `${base}/wakeups/pending`,
    tag: "runs",
    summary: "Queued wake-ups (the runtime's poll fallback; the primary path is the webhook)",
    access: "agent_key",
    params: CompanyParams,
    query: WakeupPendingQuery,
    response: { status: 200, schema: z.object({ data: z.array(WakeupRequest) }) },
    errors: [],
    emits: [],
  }),
  // WF-FIX (2026-10-26): the Wake-ups screen's count of what self-hosted DEs have not pulled yet.
  "wakeups.inbox": defineRoute({
    ...agents,
    id: "wakeups.inbox",
    method: "get",
    path: `${base}/wakeups/inbox`,
    tag: "runs",
    summary: "Queued wake-ups waiting for self-hosted DEs to pull them, per DE",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: WakeupInbox },
    errors: [],
    emits: [],
  }),
  "wakeups.dispatch": defineRoute({
    ...agents,
    id: "wakeups.dispatch",
    method: "post",
    path: `${base}/wakeups/dispatch`,
    tag: "runs",
    summary:
      "Admit queued wake-ups and return every queued run as the webhook body (the runtime's poll)",
    access: "agent_key",
    params: CompanyParams,
    body: WakeupDispatch,
    response: { status: 200, schema: z.object({ data: z.array(RuntimeWakeup) }) },
    errors: [],
    emits: [
      "run.queued",
      "budget.reserved",
      "wakeup.coalesced",
      "wakeup.dropped",
      "budget.reservation_refused",
      // Phase 2 increment 6: a shape-A run without routing evidence (`profile_unroutable`).
      "run.failed",
      "budget.settled",
    ],
  }),

  "delegationRequests.get": defineRoute({
    ...agents,
    id: "delegationRequests.get",
    method: "get",
    path: `${base}/delegation-requests/{id}`,
    tag: "runs",
    summary: "Get a delegation request",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: DelegationRequest },
    errors: [],
    emits: [],
  }),
  "delegationRequests.accept": defineRoute({
    ...agents,
    id: "delegationRequests.accept",
    method: "post",
    path: `${base}/delegation-requests/{id}/accept`,
    tag: "runs",
    summary: "Accept delegated work (an agent recipient is woken)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: DelegationRequest },
    errors: ["conflict"],
    emits: ["delegation_request.accepted", "wakeup.requested"],
  }),
  "delegationRequests.decline": defineRoute({
    ...agents,
    id: "delegationRequests.decline",
    method: "post",
    path: `${base}/delegation-requests/{id}/decline`,
    tag: "runs",
    summary: "Decline delegated work",
    access: "member",
    params: CompanyItemParams,
    body: DelegationDecline,
    idempotency: "optional",
    response: { status: 200, schema: DelegationRequest },
    errors: ["conflict"],
    emits: ["delegation_request.declined", "wakeup.requested"],
  }),
  "delegationRequests.complete": defineRoute({
    ...agents,
    id: "delegationRequests.complete",
    method: "post",
    path: `${base}/delegation-requests/{id}/complete`,
    tag: "runs",
    summary: "Hand delegated work back (the requester is woken)",
    access: "member",
    params: CompanyItemParams,
    body: DelegationComplete,
    idempotency: "optional",
    response: { status: 200, schema: DelegationRequest },
    errors: ["conflict"],
    emits: ["delegation_request.completed", "wakeup.requested"],
  }),

  "digitalEmployees.agents": defineRoute({
    ...agents,
    id: "digitalEmployees.agents",
    method: "get",
    path: `${base}/digital-employees/{id}/agents`,
    tag: "digital-employees",
    summary: "The DE's agent composition (lead, specialists; agency only)",
    access: "operator",
    params: CompanyItemParams,
    response: { status: 200, schema: z.object({ data: z.array(AgentProfile) }) },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.addAgent": defineRoute({
    ...agents,
    id: "digitalEmployees.addAgent",
    method: "post",
    path: `${base}/digital-employees/{id}/agents`,
    tag: "digital-employees",
    summary: "Add an agent profile from a template version (agency only)",
    access: "operator",
    params: CompanyItemParams,
    body: AgentProfileCreate,
    idempotency: "optional",
    response: { status: 201, schema: AgentProfile },
    errors: ["conflict"],
    emits: ["agent_profile.created"],
  }),
  "digitalEmployees.updateAgent": defineRoute({
    ...agents,
    id: "digitalEmployees.updateAgent",
    method: "patch",
    path: `${base}/digital-employees/{id}/agents/{profile_id}`,
    tag: "digital-employees",
    summary: "Re-pin, re-role or override an agent profile (agency only)",
    access: "operator",
    params: AgentProfileParams,
    body: AgentProfilePatch,
    response: { status: 200, schema: AgentProfile },
    errors: ["conflict"],
    emits: ["agent_profile.updated"],
  }),
  "digitalEmployees.retireAgent": defineRoute({
    ...agents,
    id: "digitalEmployees.retireAgent",
    method: "delete",
    path: `${base}/digital-employees/{id}/agents/{profile_id}`,
    tag: "digital-employees",
    summary: "Retire an agent profile (not the DE's last lead)",
    access: "operator",
    params: AgentProfileParams,
    response: { status: 200, schema: AgentProfile },
    errors: ["conflict"],
    emits: ["agent_profile.retired"],
  }),
  "digitalEmployees.budget": defineRoute({
    feature: "core",
    id: "digitalEmployees.budget",
    method: "get",
    path: `${base}/digital-employees/{id}/budget`,
    tag: "digital-employees",
    summary: "The DE's budget, spend and reservations this period",
    description:
      "A company manager, or a person above the DE's seat (its supervisor at any depth): the " +
      "seat drawer's budget panel and the budget notices' link.",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: DigitalEmployeeBudget },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.updateBudget": defineRoute({
    feature: "core",
    id: "digitalEmployees.updateBudget",
    method: "patch",
    path: `${base}/digital-employees/{id}/budget`,
    tag: "digital-employees",
    summary: "Set the DE's holder budget (created on first use)",
    access: "manager",
    params: CompanyItemParams,
    body: BudgetInput.omit({ subject_kind: true, subject_id: true }),
    response: { status: 200, schema: DigitalEmployeeBudget },
    errors: [],
    emits: ["budget.changed", "digital_employee.resumed"],
  }),
  "companies.updateBudgetPace": defineRoute({
    feature: "core",
    id: "companies.updateBudgetPace",
    method: "patch",
    path: `${base}/budget-pace`,
    tag: "digital-employees",
    summary: "Set the budget pace thresholds: the company default or one DE's",
    description:
      "DE-BUDGET-PACE. Replaces only the named scope inside `settings.budget_pace` — one DE's " +
      "overrides (`de_membership_id`) or the company defaults — under the company row's lock, so " +
      "two saves of different scopes (or of any other setting) never overwrite each other. An " +
      "empty `settings` clears that scope (it inherits). Returns the stored `budget_pace`.",
    access: "manager",
    params: CompanyParams,
    body: z.strictObject({
      de_membership_id: Uuid.optional(),
      settings: BudgetPaceSettings,
    }),
    response: { status: 200, schema: CompanyBudgetPace },
    errors: [],
    emits: ["company.updated"],
  }),
  "agent.budget": defineRoute({
    feature: "core",
    id: "agent.budget",
    method: "get",
    path: `${base}/agent/budget`,
    tag: "digital-employees",
    summary:
      "get_budget: the calling DE's own limit, spend, period, projected spend and pace status",
    description:
      "DE-BUDGET-PACE. A digital employee's own run token or agent key; never a person, a crew " +
      "or an ingest key. The same pace the DE's budget view shows.",
    access: "run_token",
    params: CompanyParams,
    response: { status: 200, schema: BudgetPace },
    errors: [],
    emits: [],
  }),
  "agentKeys.rotate": defineRoute({
    feature: "core",
    id: "agentKeys.rotate",
    method: "post",
    path: `${base}/agent-keys/rotate`,
    tag: "digital-employees",
    summary: "Rotate the calling key (new plaintext once; the old key stays valid 10 minutes)",
    access: "agent_key",
    params: CompanyParams,
    idempotency: "required",
    response: { status: 201, schema: AgentKeyRotated },
    errors: [],
    emits: ["agent_key.rotated", "agent_key.created"],
  }),
} as const;
